import { apiFetchWithRetry } from "./apiFetch";

export type DirectoryStatusFilter = "unverified" | "verified" | "all";

export interface SchoolDirectoryEntry {
  id: string;
  name: string;
  city: string | null;
  region: string | null;
  country: string | null;
  studentCount: number;
  source: string;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  /** Non-null when this entry has been graduated into an operational
   *  (Partner Schools) school; null for candidate names not yet onboarded. */
  linkedSchoolId: string | null;
}

export interface LinkedStudent {
  id: string;
  name: string | null;
  email: string;
  grade: string | null;
  role: string;
  status: string;
}

export interface SchoolDirectoryDetail extends SchoolDirectoryEntry {
  website: string | null;
  students: LinkedStudent[];
}

export interface ListSchoolDirectoryResponse {
  entries: SchoolDirectoryEntry[];
  /** Pass back as `cursor` on the next request. Null when there are no more pages. */
  nextCursor: string | null;
  /** Total entries matching the filter (without cursor) — for the header badge. */
  total: number;
}

export interface UpdateSchoolDirectoryPayload {
  name?: string;
  /** Empty-string clears the field (server treats it as "set to null"). */
  city?: string;
  region?: string;
  /** ISO 3166-1 alpha-2; server uppercases. Empty-string clears. */
  country?: string;
}

export interface SchoolDirectoryMessage {
  message: string;
  entry?: SchoolDirectoryEntry;
}

export const schoolDirectoryService = {
  list: async (params: {
    status?: DirectoryStatusFilter;
    search?: string;
    cursor?: string;
    limit?: number;
  } = {}): Promise<ListSchoolDirectoryResponse> => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.search) search.set("search", params.search);
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    const res = await apiFetchWithRetry(`/v1/school-directory${qs ? `?${qs}` : ""}`);
    if (!res.ok) throw new Error(`Failed to list school directory (${res.status})`);
    return (await res.json()) as ListSchoolDirectoryResponse;
  },

  getDetail: async (id: string): Promise<SchoolDirectoryDetail> => {
    const res = await apiFetchWithRetry(`/v1/school-directory/${encodeURIComponent(id)}`);
    if (!res.ok) throw new Error(`Failed to load entry (${res.status})`);
    return (await res.json()) as SchoolDirectoryDetail;
  },

  verify: async (id: string): Promise<SchoolDirectoryMessage> => {
    const res = await apiFetchWithRetry(`/v1/school-directory/${encodeURIComponent(id)}/verify`, {
      method: "POST",
    });
    if (!res.ok) throw new Error(`Failed to verify entry (${res.status})`);
    return (await res.json()) as SchoolDirectoryMessage;
  },

  update: async (id: string, payload: UpdateSchoolDirectoryPayload): Promise<SchoolDirectoryEntry> => {
    const res = await apiFetchWithRetry(`/v1/school-directory/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // Surface the server's message when present (409s carry the merge
      // hint we want to show inline on the modal).
      let serverMessage: string | undefined;
      try {
        const body = await res.json();
        serverMessage = typeof body?.message === "string" ? body.message : undefined;
      } catch {
        /* not JSON or empty body — fall through */
      }
      throw new Error(serverMessage ?? `Failed to update entry (${res.status})`);
    }
    return (await res.json()) as SchoolDirectoryEntry;
  },

  delete: async (id: string): Promise<SchoolDirectoryMessage> => {
    const res = await apiFetchWithRetry(`/v1/school-directory/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      let serverMessage: string | undefined;
      try {
        const body = await res.json();
        serverMessage = typeof body?.message === "string" ? body.message : undefined;
      } catch {
        /* fall through */
      }
      throw new Error(serverMessage ?? `Failed to delete entry (${res.status})`);
    }
    return (await res.json()) as SchoolDirectoryMessage;
  },
};
